/**
 * Brand identity for telehealth treatments — the consumer-facing brand name and
 * the PLP the brand row links to (ET-369).
 *
 * These are constants rather than catalog reads because the SFCC `brand`
 * attribute is NOT trustworthy on telehealth SKUs: it holds the MEDICATION name
 * on the branded products (`Mounjaro`, `Wegovy`, `Ozempic`, `Zepbound`,
 * `Foundayo`) and only reads `Patiently` on the compounded ones. The PDP brand
 * row rendered "Shop more Mounjaro" on both FSA and HSA as a direct result.
 * Deriving the row from these constants makes it correct by construction and
 * immune to the catalog being fixed, half-fixed, or re-broken later.
 */

/**
 * The brand every telehealth treatment is sold under, on FSA and HSA alike.
 * Not per-site and not per-treatment — there is one telehealth brand.
 */
export const TELEHEALTH_BRAND_NAME = "Patiently";

/**
 * Brand landing page the PDP brand row sends shoppers to — the Builder-authored
 * Patiently LP, served by the catch-all Builder page-tree route (verified
 * 2026-09-22 on fsa-develop / hsa-develop: 200, `<title>Patiently™ by FSA
 * Store®</title>` / `…by HSA Store®`). A sibling path that has no Builder entry
 * 404s, so the 200 is content-driven, not a catch-all shell.
 *
 * NOT the `weight-loss` PLP, which is what this row shipped with and what QA
 * passed on: the source AC in ET-216 / ET-222 reads "Display 'Shop more
 * Patiently' link to the Patiently landing page", and the PLP wording was a QA
 * paraphrase. Lilia Coburn settled it on ET-369 (2026-09-21) — the LP, "for all
 * conditions", so this stays condition-independent as the telehealth tree grows
 * past weight loss. Don't "fix" it back to a product grid.
 *
 * `/patiently.html` is not an alias (404). The trailing-slash form resolves the
 * same page, but link the bare path.
 *
 * ⚠️ Validity tracks BUILDER state — an editor unpublishing or re-slugging the
 * LP breaks this with no code change. `tests/smoke.pdp-telehealth.test.ts`
 * follows the rendered href and asserts the destination is the Patiently LP;
 * the PR pipeline runs that one test (the `e2e_smoke_scripts` anchor in
 * `bitbucket-pipelines.yml`), non-blocking.
 */
export const TELEHEALTH_BRAND_LANDING_PATH = "/patiently";

/**
 * Category PLP the header's "WEIGHT LOSS" shop-our-world row links to — the
 * page listing all eight GLP-1 launch treatments (verified 2026-09-16 on
 * fsa-develop / hsa-develop: `<title>Shop Weight Loss</title>`, 8 telehealth
 * SKUs, 8 JSON-LD Product entries).
 *
 * `/weight-loss.html` is NOT an equivalent alias, and must not be "optimised"
 * back in: SFCC URL rules 301 it to `/glp1s`, the Builder treatment landing
 * page — a DIFFERENT page, with marketing copy and zero product links.
 *
 * ⚠️ Validity tracks CATALOG state, not the `TREATMENTS` registry. Note this
 * is a category URL — a separate namespace from every id in `./treatments.ts`
 * (`slug` is the `/intake/*` route, `conditionType` the BM product attribute,
 * `planCategoryId` the plan-selection list); they merely spell the same string
 * today. So do NOT derive this from `soleTreatment()?.slug`. What actually
 * breaks it is a merchandiser renaming or retiring the `weight-loss` category,
 * or the telehealth tree growing a second landing PLP — neither of which
 * changes any code.
 *
 * One owner as of ET-369: the header's "WEIGHT LOSS" SOW row
 * (`components/hec-header/hec-header.tsx`). The PDP brand row moved to
 * `TELEHEALTH_BRAND_LANDING_PATH` above — the two destinations are no longer
 * the same page, so don't re-collapse them.
 */
export const TELEHEALTH_PLP_PATH = "/weight-loss";
